import { createContext, useContext, type ReactNode, useMemo, useState, useCallback } from "react";

interface FormValidationContextValue {
  fieldErrors: Record<string, string>;
  setFieldErrors: (errors: Record<string, string>) => void;
  clearFieldError: (fieldId: string) => void;
  uploadCount: number;
  setUploading: (active: boolean) => void;
}

const FormValidationContext = createContext<FormValidationContextValue | null>(null);

export function FormValidationProvider({ children }: { children: ReactNode }) {
  const [fieldErrors, setFieldErrorsState] = useState<Record<string, string>>({});
  const [uploadCount, setUploadCount] = useState(0);

  const setFieldErrors = useCallback((errors: Record<string, string>) => {
    setFieldErrorsState(errors);
  }, []);

  const clearFieldError = useCallback((fieldId: string) => {
    setFieldErrorsState((current) => {
      if (!current[fieldId]) return current;
      const next = { ...current };
      delete next[fieldId];
      return next;
    });
  }, []);

  const setUploading = useCallback((active: boolean) => {
    setUploadCount((count) => Math.max(0, count + (active ? 1 : -1)));
  }, []);

  const value = useMemo(
    () => ({
      fieldErrors,
      setFieldErrors,
      clearFieldError,
      uploadCount,
      setUploading,
    }),
    [fieldErrors, setFieldErrors, clearFieldError, uploadCount, setUploading]
  );

  return (
    <FormValidationContext.Provider value={value}>
      {children}
    </FormValidationContext.Provider>
  );
}

export function useFormValidation() {
  const ctx = useContext(FormValidationContext);
  if (!ctx) {
    throw new Error("useFormValidation must be used within FormValidationProvider");
  }
  return ctx;
}
